import { useCallback } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';

import type { Dispatch } from 'redux';

type Props = { [key: string]: any };

type StateToPropsMapper<State, OwnProps> = (
  state: State,
  ownProps?: OwnProps
) => Props;

type MaybeStateToPropsMapper<S, O> =
  | StateToPropsMapper<S, O>
  | undefined
  | null;

type DispatchMapper = (dispatch: Dispatch<any>) => {};

type ReduxHookValue = any;

const defaultMapper = () => ({});

export const useRedux = <State, OwnProps>(
  mapStateToProps: MaybeStateToPropsMapper<State, OwnProps>,
  mapDispatchToProps?: DispatchMapper,
  ownProps?: OwnProps
): ReduxHookValue => {
  // Memoized with useCallback per docs: https://github.com/facebookincubator/redux-react-hook#usemappedstatemapstate
  const safeMapStateToProps = mapStateToProps || defaultMapper;
  const safeMapDispatchToProps = mapDispatchToProps || defaultMapper;
  const mapState = useCallback(state => safeMapStateToProps(state, ownProps), [
    safeMapStateToProps,
    ownProps,
  ]);
  const data = useMappedState(mapState);

  const dispatch = useDispatch();
  const actions = safeMapDispatchToProps(dispatch);

  return {
    ...data,
    ...actions,
  };
};
